import React from 'react';
import styled from 'styled-components';
import ColorSwatch from '../../components/ColorSwatch';
import CodeBlock from '../../components/CodeBlock';

const PageHeader = styled.div`
  margin-bottom: 48px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
`;

const SectionDescription = styled.p`
  font-size: 15px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  line-height: 1.7;
  margin: 0 0 32px 0;
`;

const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
`;

const Colors = () => {
  return (
    <div>
      <PageHeader>
        <Title>Colors</Title>
        <Subtitle>
          Our color palette is designed to work seamlessly in both light and
          dark modes, ensuring consistent brand identity and optimal readability
          across all interfaces.
        </Subtitle>
      </PageHeader>

      <Section>
        <SectionTitle>Brand Colors</SectionTitle>
        <SectionDescription>
          Primary brand colors used for interactive elements, links, and key UI
          components throughout the application.
        </SectionDescription>
        <ColorGrid>
          <ColorSwatch
            name="Primary"
            value="#FA4616"
            usage="Main brand color for buttons, links, and accents"
          />
          <ColorSwatch
            name="Primary RGB"
            value="rgb(250, 70, 22)"
            usage="RGB variant for opacity calculations"
          />
          <ColorSwatch
            name="Secondary"
            value="#00001F"
            usage="Secondary brand color for headers and emphasis"
          />
        </ColorGrid>
        <CodeBlock>
{`// Access theme colors
const StyledButton = styled.button\`
  background: \${props => props.theme.colors.primary};
  color: white;

  &:hover {
    background: #E03E0C; // Darker shade
  }
\`;

// Using primaryOpacity function
background: \${props => props.theme.colors.primaryOpacity(0.1)};`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Semantic Colors</SectionTitle>
        <SectionDescription>
          Colors that convey meaning and status across the application. These
          are consistent in both light and dark themes.
        </SectionDescription>
        <ColorGrid>
          <ColorSwatch
            name="Success"
            value="#22C55E"
            usage="Success states, positive actions, SuperFan segment"
          />
          <ColorSwatch
            name="Error"
            value="#EF4444"
            usage="Error states, destructive actions, validation errors"
          />
          <ColorSwatch
            name="Error Alt"
            value="#DC2626"
            usage="Alternative error color, Inactive segment"
          />
          <ColorSwatch
            name="Warning"
            value="#FF9800"
            usage="Warning states, caution indicators"
          />
          <ColorSwatch
            name="Info"
            value="#2196F3"
            usage="Informational states, neutral notifications"
          />
        </ColorGrid>
      </Section>

      <Section>
        <SectionTitle>Customer Segments</SectionTitle>
        <SectionDescription>
          Distinct colors for customer segmentation in analytics and reporting.
          These colors are consistent across themes to maintain recognition.
        </SectionDescription>
        <ColorGrid>
          <ColorSwatch
            name="Super Fan"
            value="#22C55E"
            usage="High-value customers with frequent purchases"
          />
          <ColorSwatch
            name="Regular Fan"
            value="#3B82F6"
            usage="Regular customers with consistent engagement"
          />
          <ColorSwatch
            name="Casual Fan"
            value="#06B6D4"
            usage="Occasional customers"
          />
          <ColorSwatch
            name="First-Timer"
            value="#F59E0B"
            usage="New customers, first purchase"
          />
          <ColorSwatch
            name="Inactive"
            value="#DC2626"
            usage="Dormant customers, no recent activity"
          />
        </ColorGrid>
        <CodeBlock>
{`// Access segment colors
import { useTheme } from 'styled-components';

const theme = useTheme();
const segmentColor = theme.colors.segments.superFan; // #22C55E`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Background Colors (Dark Theme)</SectionTitle>
        <SectionDescription>
          Layered background colors for depth and hierarchy in dark mode.
        </SectionDescription>
        <ColorGrid>
          <ColorSwatch
            name="Primary Background"
            value="linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)"
            usage="Main page background gradient"
          />
          <ColorSwatch
            name="Secondary Background"
            value="#1A1A2E"
            usage="Secondary surfaces"
          />
          <ColorSwatch
            name="Card Background"
            value="rgba(255, 255, 255, 0.05)"
            usage="Card and panel backgrounds"
          />
          <ColorSwatch
            name="Overlay Background"
            value="rgba(255, 255, 255, 0.03)"
            usage="Subtle overlays and hover states"
          />
        </ColorGrid>
      </Section>

      <Section>
        <SectionTitle>Background Colors (Light Theme)</SectionTitle>
        <SectionDescription>
          Clean, minimal backgrounds for light mode interfaces.
        </SectionDescription>
        <ColorGrid>
          <ColorSwatch
            name="Primary Background"
            value="#F8FAFC"
            usage="Main page background"
          />
          <ColorSwatch
            name="Secondary Background"
            value="#F2F2F2"
            usage="Secondary surfaces"
          />
          <ColorSwatch
            name="Card Background"
            value="rgba(255, 255, 255, 0.95)"
            usage="Card and panel backgrounds"
          />
          <ColorSwatch
            name="Overlay Background"
            value="rgba(0, 0, 0, 0.03)"
            usage="Subtle overlays and hover states"
          />
        </ColorGrid>
      </Section>

      <Section>
        <SectionTitle>Text Colors</SectionTitle>
        <SectionDescription>
          Standardized text colors for consistent readability and hierarchy.
          These colors adapt between light and dark themes.
        </SectionDescription>
        <ColorGrid>
          <ColorSwatch
            name="Text Primary (Dark)"
            value="#FFFFFF"
            usage="Main text in dark theme"
          />
          <ColorSwatch
            name="Text Secondary (Dark)"
            value="#94A3B8"
            usage="Secondary text in dark theme"
          />
          <ColorSwatch
            name="Text Muted (Dark)"
            value="#64748B"
            usage="Muted text in dark theme"
          />
          <ColorSwatch
            name="Text Primary (Light)"
            value="#0F172A"
            usage="Main text in light theme"
          />
          <ColorSwatch
            name="Text Secondary (Light)"
            value="#64748B"
            usage="Secondary text in light theme"
          />
          <ColorSwatch
            name="Text Muted (Light)"
            value="#94A3B8"
            usage="Muted text in light theme"
          />
        </ColorGrid>
        <CodeBlock>
{`// Text colors automatically adapt to theme
const Text = styled.p\`
  color: \${props => props.theme.colors.text.primary};

  // Muted variant
  &.muted {
    color: \${props => props.theme.colors.text.muted};
  }
\`;`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Border Colors</SectionTitle>
        <SectionDescription>
          Subtle borders that provide separation without overwhelming the
          interface.
        </SectionDescription>
        <ColorGrid>
          <ColorSwatch
            name="Border (Dark)"
            value="rgba(255, 255, 255, 0.1)"
            usage="Standard borders in dark theme"
          />
          <ColorSwatch
            name="Border Light (Dark)"
            value="rgba(255, 255, 255, 0.08)"
            usage="Lighter borders in dark theme"
          />
          <ColorSwatch
            name="Border (Light)"
            value="#E2E8F0"
            usage="Standard borders in light theme"
          />
          <ColorSwatch
            name="Border Light (Light)"
            value="#F1F5F9"
            usage="Lighter borders in light theme"
          />
        </ColorGrid>
      </Section>

      <Section>
        <SectionTitle>Usage Example</SectionTitle>
        <SectionDescription>
          Complete example of using theme colors in a component.
        </SectionDescription>
        <CodeBlock>
{`import styled from 'styled-components';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';

const Container = styled.div\`
  background: \${props =>
    props.theme.name === 'dark'
      ? props.theme.colors.background.card
      : props.theme.colors.card.background
  };
  border: 1px solid \${props => props.theme.colors.border};
  border-radius: 8px;
  padding: 24px;
\`;

const Title = styled.h2\`
  color: \${props => props.theme.colors.text.primary};
  margin-bottom: 8px;
\`;

const Description = styled.p\`
  color: \${props => props.theme.colors.text.secondary};
  line-height: 1.6;
\`;

const PrimaryButton = styled.button\`
  background: \${props => props.theme.colors.primary};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;

  &:hover {
    background: #E03E0C;
  }
\`;

const MyComponent = () => (
  <EnhancedThemeProvider>
    <Container>
      <Title>Welcome</Title>
      <Description>This component adapts to light/dark themes</Description>
      <PrimaryButton>Get Started</PrimaryButton>
    </Container>
  </EnhancedThemeProvider>
);`}
        </CodeBlock>
      </Section>
    </div>
  );
};

export default Colors;